import React, {useEffect, useState} from 'react';
import Alert from '../../Alert';
import {useAuth} from '../../../helpers/AuthContext';
import HelpArticleLink from '../HelpArticleLink';
import ResourceAvailabilityModal from '../Booking/ResourceAvailabilityModal';
import bookingPriceExample from './bookingPriceExample';

const MODE_OPTIONS = [
    {value: 'appointment', label: 'Appointment'},
    {value: 'daterange', label: 'Date range / overnight stay'},
    {value: 'pickup', label: 'Pickup window'},
];

const RESOURCE_TYPE_LABELS = {
    staff: 'Staff',
    space: 'Space',
    asset: 'Asset',
    pickup: 'Pickup',
    seller: 'Calendar',
};

const readResponse = async response => {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch (error) {
        return {message: text};
    }
};

const responseMessage = (body, fallback) => body?.error || body?.message || fallback;

function NumberField({label, name, value, onChange, help, min = 0, max, step = 1}) {
    const fieldId = `booking-${name}`;
    return (
        <div className="group">
            <label htmlFor={fieldId}>{label}</label>
            <input
                id={fieldId}
                type="number"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={event => onChange(event.target.value)}
            />
            {help && <p className="help">{help}</p>}
        </div>
    );
}

function SelectField({label, name, value, options, onChange, help}) {
    const fieldId = `booking-${name}`;
    return (
        <div className="group">
            <label htmlFor={fieldId}>{label}</label>
            <select
                id={fieldId}
                value={value}
                onChange={event => onChange(event.target.value)}
            >
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {help && <p className="help">{help}</p>}
        </div>
    );
}

function TimeField({label, name, value, onChange}) {
    const fieldId = `booking-${name}`;
    return (
        <div className="group">
            <label htmlFor={fieldId}>{label}</label>
            <input
                id={fieldId}
                type="time"
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </div>
    );
}

export default function ProductBookingSettings({value, onChange, price, currency}) {
    const {fetch, API_URL = ''} = useAuth();
    const [resources, setResources] = useState([]);
    const [resourceStatus, setResourceStatus] = useState('idle');
    const [resourceError, setResourceError] = useState('');
    const [resourceReload, setResourceReload] = useState(0);
    const [availabilityResource, setAvailabilityResource] = useState(null);

    const update = patch => onChange({...value, ...patch});

    useEffect(() => {
        if (!value.enabled) return;

        let active = true;
        const loadResources = async () => {
            setResourceStatus('loading');
            setResourceError('');
            try {
                const listResponse = await fetch(`${API_URL}/v1/seller/booking/resources`);
                const listBody = await readResponse(listResponse);
                if (!listResponse.ok) {
                    throw new Error(responseMessage(listBody, 'Could not load calendars.'));
                }

                let activeResources = (listBody.data || []).filter(resource => resource.isActive);
                if (activeResources.length === 0) {
                    const createResponse = await fetch(
                        `${API_URL}/v1/seller/booking/resources/ensure-default`,
                        {method: 'POST'},
                    );
                    const createBody = await readResponse(createResponse);
                    if (!createResponse.ok) {
                        throw new Error(
                            responseMessage(createBody, 'Could not create a default calendar.'),
                        );
                    }
                    activeResources = createBody.data ? [createBody.data] : [];
                }

                if (active) {
                    setResources(activeResources);
                    setResourceStatus('ready');
                }
            } catch (error) {
                if (active) {
                    setResourceError(error.message);
                    setResourceStatus('error');
                }
            }
        };

        loadResources();
        return () => {
            active = false;
        };
    }, [API_URL, fetch, resourceReload, value.enabled]);

    useEffect(() => {
        if (!value.enabled || resourceStatus !== 'ready'
            || value.resourceIds.length > 0 || resources.length === 0) {
            return;
        }
        update({resourceIds: resources.map(resource => resource.id)});
    }, [resourceStatus, resources, value.enabled, value.resourceIds.length]);

    const setMode = mode => {
        const pricingUnit = mode === 'daterange' ? 'night'
            : mode === 'pickup' ? 'slot'
                : (parseInt(value.maxAttendees, 10) > 1 ? 'attendee' : 'slot');
        update({mode, pricingUnit});
    };

    const toggleResource = resourceId => {
        const selected = value.resourceIds.includes(resourceId);
        update({
            resourceIds: selected
                ? value.resourceIds.filter(id => id !== resourceId)
                : [...value.resourceIds, resourceId],
        });
    };

    const updateFee = (index, patch) => {
        update({
            fees: value.fees.map((fee, feeIndex) => (
                feeIndex === index ? {...fee, ...patch} : fee
            )),
        });
    };

    const removeFee = index => {
        update({fees: value.fees.filter((fee, feeIndex) => feeIndex !== index)});
    };

    return (
        <div className="booking-settings-editor">
            {value.enabled && (
                <p className="help">
                    <HelpArticleLink
                        slug="booking-and-scheduling"
                        label="Help with bookings and scheduling"
                        variant="link"
                    />
                </p>
            )}
            <div className="booking-settings__grid">
                <SelectField
                    label="Booking type"
                    name="mode"
                    value={value.mode}
                    options={MODE_OPTIONS}
                    onChange={setMode}
                />
                <SelectField
                    label="Confirmation"
                    name="confirmation"
                    value={value.confirmation}
                    options={[
                        {value: 'instant', label: 'Confirm after payment'},
                        {value: 'request', label: 'Request my approval'},
                    ]}
                    onChange={confirmation => update({confirmation})}
                    help="Requests remain pending until you approve or decline them."
                />
                <SelectField
                    label="Times shown in"
                    name="timezoneDisplay"
                    value={value.timezoneDisplay}
                    options={[
                        {value: 'resource', label: 'Calendar timezone'},
                        {value: 'shopper', label: 'Shopper timezone'},
                    ]}
                    onChange={timezoneDisplay => update({timezoneDisplay})}
                />
            </div>

            <fieldset className="booking-settings__fieldset">
                <legend>Calendars and resources</legend>
                {resourceStatus === 'loading' && (
                    <p className="help" aria-live="polite">Loading calendars…</p>
                )}
                {resourceError && (
                    <>
                        <Alert type="error">{resourceError}</Alert>
                        <button
                            type="button"
                            className="button secondary small"
                            onClick={() => setResourceReload(current => current + 1)}
                        >
                            Try again
                        </button>
                    </>
                )}
                {resourceStatus === 'ready' && resources.map(resource => (
                    <div className="booking-settings__resource-row" key={resource.id}>
                        <label
                            className="checkbox booking-settings__resource"
                            htmlFor={`booking-resource-${resource.id}`}
                        >
                            <input
                                id={`booking-resource-${resource.id}`}
                                type="checkbox"
                                checked={value.resourceIds.includes(resource.id)}
                                onChange={() => toggleResource(resource.id)}
                            />
                            <span>
                                <strong>{resource.name}</strong>
                                {' · '}
                                {RESOURCE_TYPE_LABELS[resource.type] || resource.type}
                                {' · '}
                                {resource.timezone}
                                {resource.capacity > 1
                                    ? ` · Capacity ${resource.capacity}`
                                    : ''}
                            </span>
                        </label>
                        <button
                            type="button"
                            className="button secondary small"
                            onClick={() => setAvailabilityResource(resource)}
                        >
                            Manage Calendar and Availability
                        </button>
                    </div>
                ))}
                <p className="help">
                    Availability and timezone come from the selected calendars.
                </p>
                {value.enabled && resourceStatus === 'ready'
                    && (resources.length === 0 || value.resourceIds.length === 0) && (
                    <p className="help">
                        No calendar is selected yet. Use “Manage Calendar and Availability”
                        above, or open the Bookings &amp; scheduling console at{' '}
                        <a href="/account/bookings">
                            the Bookings &amp; scheduling console
                        </a>{' '}
                        to set up your
                        calendars and availability.
                    </p>
                )}
                {resources.length > 1 && (
                    <SelectField
                        label="Calendar choice"
                        name="resourceStrategy"
                        value={value.resourceStrategy}
                        options={[
                            {value: 'any', label: 'Assign any available calendar'},
                            {value: 'specific', label: 'Let the shopper choose'},
                        ]}
                        onChange={resourceStrategy => update({resourceStrategy})}
                    />
                )}
            </fieldset>

            <fieldset className="booking-settings__fieldset">
                <legend>Timing</legend>
                <div className="booking-settings__grid">
                    {value.mode === 'appointment' && (
                        <>
                            <NumberField
                                label="Appointment length (minutes)"
                                name="durationMinutes"
                                value={value.durationMinutes}
                                min={5}
                                max={1440}
                                onChange={durationMinutes => update({durationMinutes})}
                            />
                            <NumberField
                                label="Start times every (minutes)"
                                name="granularityMinutes"
                                value={value.granularityMinutes}
                                min={5}
                                max={1440}
                                onChange={granularityMinutes => update({
                                    granularityMinutes,
                                })}
                            />
                            <NumberField
                                label="Buffer before (minutes)"
                                name="bufferBeforeMinutes"
                                value={value.bufferBeforeMinutes}
                                max={720}
                                onChange={bufferBeforeMinutes => update({
                                    bufferBeforeMinutes,
                                })}
                            />
                            <NumberField
                                label="Buffer after (minutes)"
                                name="bufferAfterMinutes"
                                value={value.bufferAfterMinutes}
                                max={720}
                                onChange={bufferAfterMinutes => update({
                                    bufferAfterMinutes,
                                })}
                            />
                            <NumberField
                                label="Maximum attendees"
                                name="maxAttendees"
                                value={value.maxAttendees}
                                min={1}
                                max={1000}
                                onChange={maxAttendees => update({
                                    maxAttendees,
                                    pricingUnit: parseInt(maxAttendees, 10) > 1
                                        ? value.pricingUnit
                                        : 'slot',
                                })}
                                help="Use more than 1 for classes or group sessions."
                            />
                        </>
                    )}
                    {value.mode === 'daterange' && (
                        <>
                            <NumberField
                                label="Minimum nights"
                                name="minNights"
                                value={value.minNights}
                                min={1}
                                max={365}
                                onChange={minNights => update({minNights})}
                            />
                            <NumberField
                                label="Maximum nights"
                                name="maxNights"
                                value={value.maxNights}
                                min={1}
                                max={365}
                                onChange={maxNights => update({maxNights})}
                            />
                            <TimeField
                                label="Check-in time"
                                name="checkinTime"
                                value={value.checkinTime}
                                onChange={checkinTime => update({checkinTime})}
                            />
                            <TimeField
                                label="Check-out time"
                                name="checkoutTime"
                                value={value.checkoutTime}
                                onChange={checkoutTime => update({checkoutTime})}
                            />
                        </>
                    )}
                    {value.mode === 'pickup' && (
                        <NumberField
                            label="Pickup window length (minutes)"
                            name="windowMinutes"
                            value={value.windowMinutes}
                            min={5}
                            max={1440}
                            onChange={windowMinutes => update({windowMinutes})}
                        />
                    )}
                    <NumberField
                        label="Minimum notice (minutes)"
                        name="minNoticeMinutes"
                        value={value.minNoticeMinutes}
                        min={0}
                        max={129600}
                        onChange={minNoticeMinutes => update({minNoticeMinutes})}
                    />
                    <NumberField
                        label="Book up to (days ahead)"
                        name="maxAdvanceDays"
                        value={value.maxAdvanceDays}
                        min={1}
                        max={730}
                        onChange={maxAdvanceDays => update({maxAdvanceDays})}
                    />
                </div>
            </fieldset>

            <fieldset className="booking-settings__fieldset">
                <legend>Pricing and cancellation</legend>
                <SelectField
                    label="Product price applies per"
                    name="pricingUnit"
                    value={value.pricingUnit}
                    options={value.mode === 'daterange'
                        ? [{value: 'night', label: 'Night'}]
                        : value.mode === 'appointment' && value.maxAttendees > 1
                            ? [
                                {value: 'slot', label: 'Booking (one price for the group)'},
                                {value: 'attendee', label: 'Attendee (price × attendees)'},
                            ]
                            : [{
                                value: 'slot',
                                label: value.mode === 'pickup'
                                    ? 'Pickup window'
                                    : 'Appointment',
                            }]}
                    onChange={pricingUnit => update({pricingUnit})}
                    help={bookingPriceExample({
                        price,
                        currency,
                        mode: value.mode,
                        pricingUnit: value.pricingUnit,
                        maxAttendees: value.maxAttendees,
                        minNights: value.minNights,
                        fees: value.fees,
                    })}
                />

                <div className="booking-settings__fees">
                    <div className="booking-settings__subheading">
                        <h4>Fixed fees</h4>
                        <button
                            type="button"
                            className="button secondary small"
                            onClick={() => update({
                                fees: [
                                    ...value.fees,
                                    {label: '', amount: '', refundable: false},
                                ],
                            })}
                        >
                            Add fee
                        </button>
                    </div>
                    {value.fees.length === 0 && (
                        <p className="help">Optional, for deposits or cleaning fees.</p>
                    )}
                    {value.fees.map((fee, index) => (
                        <div className="booking-settings__fee" key={index}>
                            <div className="group">
                                <label htmlFor={`booking-fee-label-${index}`}>
                                    Fee label
                                </label>
                                <input
                                    id={`booking-fee-label-${index}`}
                                    value={fee.label}
                                    maxLength={60}
                                    onChange={event => updateFee(index, {
                                        label: event.target.value,
                                    })}
                                />
                            </div>
                            <NumberField
                                label="Amount (USD)"
                                name={`fee-amount-${index}`}
                                value={fee.amount}
                                min={0}
                                step={0.01}
                                onChange={amount => updateFee(index, {amount})}
                            />
                            <label
                                className="checkbox booking-settings__fee-refundable"
                                htmlFor={`booking-fee-refundable-${index}`}
                            >
                                <input
                                    id={`booking-fee-refundable-${index}`}
                                    type="checkbox"
                                    checked={fee.refundable}
                                    onChange={event => updateFee(index, {
                                        refundable: event.target.checked,
                                    })}
                                />
                                <span>Refundable</span>
                            </label>
                            <button
                                type="button"
                                className="button secondary small"
                                onClick={() => removeFee(index)}
                                aria-label={`Remove fee ${index + 1}`}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>

                <label className="checkbox" htmlFor="booking-cancellation-enabled">
                    <input
                        id="booking-cancellation-enabled"
                        type="checkbox"
                        checked={value.cancellationEnabled}
                        onChange={event => update({
                            cancellationEnabled: event.target.checked,
                        })}
                    />
                    <span>Offer cancellation refunds</span>
                </label>
                {value.cancellationEnabled && (
                    <div className="booking-settings__grid">
                        <NumberField
                            label="Full refund until (hours before)"
                            name="freeUntilHours"
                            value={value.freeUntilHours}
                            min={0}
                            max={8760}
                            onChange={freeUntilHours => update({freeUntilHours})}
                        />
                        <NumberField
                            label="Late cancellation refund (%)"
                            name="refundPercent"
                            value={value.refundPercent}
                            min={0}
                            max={100}
                            onChange={refundPercent => update({refundPercent})}
                        />
                    </div>
                )}
            </fieldset>

            {availabilityResource && (
                <ResourceAvailabilityModal
                    resource={availabilityResource}
                    onSaved={(updated) => {
                        setAvailabilityResource(updated);
                        setResources(current => current.map(resource => (
                            resource.id === updated.id ? updated : resource
                        )));
                    }}
                    onClose={() => setAvailabilityResource(null)}
                />
            )}
        </div>
    );
}
